<script setup>
import { onBeforeUnmount, onBeforeMount, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { auth, database } from '@/firebase'
import { sendEmailVerification } from 'firebase/auth';
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { ref as dbRef, set } from 'firebase/database'

import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "../components/ArgonAlert.vue";

const body = document.getElementsByTagName("body")[0];

const store = useStore();
onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  body.classList.remove("bg-gray-100");
});
onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  body.classList.add("bg-gray-100");
});

const name = ref('');
const email = ref('');
const password = ref('');
const errors = ref({
  name: '',
  email: '',
  password: ''
});
const loading = ref(false);

const alertMessage = ref('');
const alertColor = ref('danger');
const showAlert = ref(false);

const router = useRouter();

const validateUser = () => {
  const newErrors = {};
  if (!name.value.trim()) {
    newErrors.name = 'Bắt buộc phải có tên!';
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email.value.trim()) {
    newErrors.email = "Bắt buộc phải có email!";
  } else if (!emailRegex.test(email.value)) {
    newErrors.email = "Định dạng email không hợp lệ!";
  }
  if (!password.value.trim()) {
    newErrors.password = 'Bắt buộc phải có mật khẩu!';
  }
  if (password.value && password.value.length < 6) {
    newErrors.password = 'Mật khẩu phải có ít nhất 6 ký tự!';
  }

  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};
const register = async () => {
  if (!validateUser()) return;
  loading.value = true;

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value);
    const user = userCredential.user;

    // Gửi email xác thực
    await sendEmailVerification(user);

    // Lưu dữ liệu người dùng vào Realtime Database
    await set(dbRef(database, 'users/' + user.uid), {
      name: name.value,
      email: email.value,
      role: 'user',
      createdAt: new Date().toISOString()
    });

    alertMessage.value = 'Đăng ký thành công! Vui lòng kiểm tra email để xác thực tài khoản.';
    alertColor.value = 'success';
    showAlert.value = true;

    // Reset form
    name.value = '';
    email.value = '';
    password.value = '';

    loading.value = false;

    // Chuyển hướng sau vài giây
    setTimeout(() => {
      router.push({ path: "/signin" });
    }, 3000);

  } catch (err) {
    loading.value = false;
    showAlert.value = true;

    if (err.code === 'auth/email-already-in-use') {
      alertMessage.value = 'Email đã được sử dụng. Vui lòng thử lại với email khác.';
    } else {
      alertMessage.value = 'Đăng ký không thành công. Vui lòng thử lại.';
    }

    alertColor.value = 'danger';
  }
};
</script>

<template>
  <div class="container top-0 position-sticky z-index-sticky">
    <div class="row">
      <div class="col-12">
        <navbar isBtn="bg-gradient-light" />
      </div>
    </div>
  </div>

  <main class="main-content mt-0">
    <div class="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg"
      style="background-image: url(https://polyschool.fpt.edu.vn/wp-content/uploads/DD_08114-FILEminimizer.jpg); background-position: top;">
      <span class="mask bg-gradient-dark opacity-6"></span>
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-5 text-center mx-auto">
            <h1 class="text-white mb-2 mt-5">FPT Polyschool</h1>
            <p class="text-lead text-white">
              Sử dụng các biểu mẫu tuyệt vời này để đăng nhập hoặc tạo tài khoản mới miễn phí.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
        <div class="col-xl-4 col-lg-5 col-md-7 mx-auto">
          <div v-if="showAlert" class="mb-4">
            <ArgonAlert :color="alertColor" dismissible>
              {{ alertMessage }}
            </ArgonAlert>
          </div>
          <div class="card z-index-0">
            <div class="card-header text-center pt-4">
              <h5>Đăng ký</h5>
            </div>
            <div class="card-body">
              <form @submit.prevent="register" role="form">
                <div class="mb-3">
                  <argon-input v-model="name" id="name" type="text" placeholder="Tên" aria-label="Name" />
                  <span class="text-danger text-sm" v-if="errors.name">{{ errors.name }}</span>
                </div>

                <div class="mb-3">
                  <argon-input v-model="email" id="email" type="text" placeholder="Email" aria-label="Email" />
                  <span class="text-danger text-sm" v-if="errors.email">{{ errors.email }}</span>
                </div>

                <div class="mb-3">
                  <argon-input v-model="password" id="password" type="password" placeholder="Mật khẩu" aria-label="Password" />
                  <span class="text-danger text-sm" v-if="errors.password">{{ errors.password }}</span>
                </div>

                <argon-button
                    :disabled="loading"
                    fullWidth
                    color="dark"
                    variant="gradient"
                    class="my-4 mb-2"
                >
                  {{ loading ? 'Đang đăng ký...' : 'Đăng ký' }}
                </argon-button>

                <p class="text-sm mt-3 mb-0">
                  Bạn đã có tài khoản?
                  <RouterLink class="text-warning font-weight-bolder" to="/signin">Đăng nhập</RouterLink>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
